// Описание анкеты карты («О себе») — разбор текста из WhatsApp в дерево узлов.
//
// Данные в базе не меняются: разбор — только при показе. Никакого HTML:
// дерево из строк, жирного, курсива и ссылок, React-узлы строит AboutText.
//
// Правила разметки (как в WhatsApp), только *жирный* и _курсив_:
//   — маркер парный и в пределах одной строки;
//   — открывающий: после начала строки, пробела или знака (не буквы/цифры)
//     и не перед пробелом;
//   — закрывающий: не после пробела, перед концом строки, пробелом или знаком;
//   — непарные маркеры остаются как есть: «2*3*4», snake_case, a_b@mail.az.
//
// Ссылки: только http:// и https://. Ссылки соцсетей уходят из текста в ряд
// кнопок под описанием, остальные — ссылка с подписью-доменом.
// Проверка — scripts/verify-tutor-slug-markdown.mts.

export type AboutNode =
  | { t: "text"; v: string }
  | { t: "b"; c: AboutNode[] }
  | { t: "i"; c: AboutNode[] }
  | { t: "link"; href: string; label: string };

export type SocialService = "youtube" | "instagram" | "whatsapp" | "telegram" | "facebook";
export type SocialLink = { service: SocialService; href: string };

export const SOCIAL_LABEL: Record<SocialService, string> = {
  youtube: "YouTube",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  facebook: "Facebook",
};

type Lang = "az" | "ru" | "en";

/**
 * Что за ссылка внутри сервиса — по адресу:
 *   — chat.whatsapp.com/… — группа, whatsapp.com/channel/… — канал,
 *     wa.me/<номер> — личный номер («WhatsApp»);
 *   — t.me/+… и t.me/joinchat/… — группа (приглашение); t.me/s/<имя> и любой
 *     другой t.me/<имя> — канал (по адресу канал, группа и личный аккаунт
 *     неотличимы, решение владельца — считать каналом).
 */
export function socialKind(l: SocialLink): "group" | "channel" | null {
  let u: URL;
  try {
    u = new URL(l.href);
  } catch {
    return null;
  }
  const h = host(u);
  if (l.service === "whatsapp") {
    if (h === "chat.whatsapp.com") return "group";
    return h === "whatsapp.com" && u.pathname.startsWith("/channel/") ? "channel" : null;
  }
  if (l.service === "telegram") {
    if (/^\/(\+|joinchat\/)/.test(u.pathname)) return "group";
    return u.pathname.length > 1 ? "channel" : null;
  }
  return null;
}

const KIND_LABEL: Record<
  Lang,
  Record<"whatsapp" | "telegram", Record<"group" | "channel", string>>
> = {
  az: {
    whatsapp: { group: "WhatsApp qrupu", channel: "WhatsApp kanalı" },
    telegram: { group: "Telegram qrupu", channel: "Telegram kanalı" },
  },
  ru: {
    whatsapp: { group: "Группа WhatsApp", channel: "Канал WhatsApp" },
    telegram: { group: "Группа Telegram", channel: "Канал Telegram" },
  },
  en: {
    whatsapp: { group: "WhatsApp group", channel: "WhatsApp channel" },
    telegram: { group: "Telegram group", channel: "Telegram channel" },
  },
};

/** Подпись кнопки: название сервиса, у групп и каналов — «Группа WhatsApp» и т. п. */
export function socialLabel(l: SocialLink, lang: Lang): string {
  const k = socialKind(l);
  if (k && (l.service === "whatsapp" || l.service === "telegram"))
    return KIND_LABEL[lang][l.service][k];
  return SOCIAL_LABEL[l.service];
}

const SOCIAL_HOSTS: Array<[RegExp, SocialService]> = [
  [/(^|\.)youtube\.com$|^youtu\.be$/, "youtube"],
  [/(^|\.)instagram\.com$|^instagr\.am$/, "instagram"],
  [/^chat\.whatsapp\.com$|^wa\.me$|^(api\.)?whatsapp\.com$/, "whatsapp"],
  [/^t\.me$|^telegram\.me$/, "telegram"],
  [/(^|\.)facebook\.com$|^fb\.com$|^fb\.me$/, "facebook"],
];

/** Трекинг-параметры: отрезаются. Смысловые (youtube v, list, t) остаются. */
const TRACKING =
  /^(utm_.+|fbclid|gclid|dclid|msclkid|yclid|igsh|igshid|si|feature|mibextid|ref|ref_src|ref_url|_ga|mc_cid|mc_eid)$/i;

const host = (u: URL) => u.hostname.toLowerCase().replace(/^www\./, "");

/** URL без трекинг-хвоста. Не http/https или не разбирается — null. */
export function cleanUrl(raw: string): string | null {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  for (const k of [...u.searchParams.keys()]) if (TRACKING.test(k)) u.searchParams.delete(k);
  let s = u.toString();
  // «?» без параметров и «/» в конце пустого пути — не нужны.
  if (!u.search) s = s.replace(/\?(?=#|$)/, "");
  if (u.pathname === "/" && !u.search && !u.hash) s = s.replace(/\/$/, "");
  return s;
}

export function socialOf(href: string): SocialService | null {
  try {
    const h = host(new URL(href));
    for (const [re, svc] of SOCIAL_HOSTS) if (re.test(h)) return svc;
  } catch {
    /* не URL */
  }
  return null;
}

/** Подпись обычной ссылки — домен без www. */
export const linkLabel = (href: string) => host(new URL(href));

const URL_RE = /https?:\/\/[^\s<>"'«»]+/gi;
/** Хвост, который почти всегда знак препинания, а не часть адреса. */
const TRAIL = /[.,;:!?)\]}*]+$/;

const isWord = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);
const isSpace = (ch: string | undefined) => !ch || /\s/u.test(ch);

/** Ссылки — неделимые знаки из частной области Unicode: маркеры внутри адреса не трогаются. */
const PH_BASE = 0xe000;

function parseInline(s: string, links: AboutNode[]): AboutNode[] {
  const out: AboutNode[] = [];
  let buf = "";
  const flush = () => {
    if (!buf) return;
    for (const part of buf.split(/([\ue000-\uf8ff])/u)) {
      if (!part) continue;
      const code = part.codePointAt(0)!;
      if (part.length === 1 && code >= PH_BASE && code <= 0xf8ff && links[code - PH_BASE])
        out.push(links[code - PH_BASE]);
      else out.push({ t: "text", v: part });
    }
    buf = "";
  };
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if ((ch === "*" || ch === "_") && !isWord(s[i - 1]) && !isSpace(s[i + 1]) && s[i + 1] !== ch) {
      let close = -1;
      for (let j = i + 2; j < s.length; j++) {
        if (s[j] === ch && !isSpace(s[j - 1]) && !isWord(s[j + 1])) {
          close = j;
          break;
        }
      }
      if (close > 0) {
        flush();
        out.push({ t: ch === "*" ? "b" : "i", c: parseInline(s.slice(i + 1, close), links) });
        i = close;
        continue;
      }
    }
    buf += ch;
  }
  flush();
  return out;
}

export type ParsedAbout = { lines: AboutNode[][]; socials: SocialLink[] };

export function parseAbout(text: string): ParsedAbout {
  const socials: SocialLink[] = [];
  const seen = new Set<string>();
  const rawLines = text.replace(/\r\n?/g, "\n").split("\n");
  const kept: Array<{ line: string; links: AboutNode[]; hadSocial: boolean }> = [];
  for (const raw of rawLines) {
    const links: AboutNode[] = [];
    let hadSocial = false;
    const line = raw.replace(URL_RE, (m) => {
      const trail = TRAIL.exec(m)?.[0] ?? "";
      const urlPart = trail ? m.slice(0, -trail.length) : m;
      const href = cleanUrl(urlPart);
      if (!href) return m;
      const svc = socialOf(href);
      if (svc) {
        hadSocial = true;
        if (!seen.has(href)) {
          seen.add(href);
          socials.push({ service: svc, href });
        }
        return trail;
      }
      links.push({ t: "link", href, label: linkLabel(href) });
      return String.fromCharCode(PH_BASE + links.length - 1) + trail;
    });
    // Строка, где была только ссылка соцсети, уходит целиком — без пустой строки.
    if (hadSocial && !line.replace(/[\s*_.,;:!?()\-–—|]+/gu, "")) continue;
    kept.push({ line: hadSocial ? line.replace(/[ \t]+$/u, "") : line, links, hadSocial });
  }
  // Пустые строки: подряд — одна, в начале и в конце — ни одной.
  const lines: AboutNode[][] = [];
  let blank = 0;
  for (const k of kept) {
    if (!k.line.trim()) {
      blank++;
      continue;
    }
    if (blank && lines.length) lines.push([]);
    blank = 0;
    lines.push(parseInline(k.line, k.links));
  }
  return { lines, socials };
}

/**
 * Длинное ли описание — решается по тексту, одинаково на сервере и в браузере
 * (кнопка «Ещё» есть сразу, без скачка вёрстки). Строка экрана телефона —
 * около 38 знаков; больше 8 строк экрана — сворачиваем.
 */
export const ABOUT_CLAMP_LINES = 8;
export function aboutIsLong(p: ParsedAbout, charsPerLine = 38): boolean {
  let n = 0;
  for (const line of p.lines) n += Math.max(1, Math.ceil(textOf(line).length / charsPerLine));
  return n > ABOUT_CLAMP_LINES;
}

export function textOf(nodes: AboutNode[]): string {
  return nodes.map((n) => (n.t === "text" ? n.v : n.t === "link" ? n.label : textOf(n.c))).join("");
}
